import {
  LAUNCHER_LABELS,
  parseLaunchTarget,
  type LaunchTarget,
  type LaunchTargetProblem,
} from '@gc/shared';

import type { FieldError } from './useSettings';

/**
 * What the "Path or launch link" field says under itself as it is typed
 * (owner, 2026-09-09: offline games take a real path, launcher games take a
 * link). The shared parser decides WHAT a string is; this module decides what
 * to SAY about it, as a locale key plus params so the same line renders in
 * English, Arabic and Kurdish. No DOM, so it is unit-tested in plain node.
 */

export type LaunchTargetPresentation =
  /** Blank field: nothing to say. */
  | { tone: 'none' }
  /** Recognised — a launcher link, a program, or a shortcut. */
  | { tone: 'ok'; key: string; params?: Record<string, string> }
  /** Refused, with the problem's own sentence. */
  | { tone: 'problem'; key: string; problem: LaunchTargetProblem };

const KEY_PREFIX = 'settings.paths';

export function problemKey(problem: LaunchTargetProblem): string {
  return `${KEY_PREFIX}.problem.${problem}`;
}

/** Locale key + params for a field value, or `none` for blank. */
export function presentLaunchTarget(value: string): LaunchTargetPresentation {
  if (value.trim().length === 0) return { tone: 'none' };
  return presentParsed(parseLaunchTarget(value));
}

/**
 * The live line, which runs on every keystroke: a value that is still the
 * BEGINNING of something — `https`, `steam:/`, `apps.microsoft.com`, a lone
 * drive letter — is not yet wrong, and a red "use the full path" under a
 * half-typed web address was the owner's first complaint (2026-09-10). Such
 * a value says nothing until it is finished; the submit gate still runs the
 * full check.
 */
export function presentWhileTyping(value: string): LaunchTargetPresentation {
  const shown = presentLaunchTarget(value);
  if (shown.tone === 'problem' && looksUnfinished(value.trim())) return { tone: 'none' };
  return shown;
}

/** A scheme, a host name or a drive letter with nothing after it yet. */
export function looksUnfinished(value: string): boolean {
  return (
    /^[A-Za-z][A-Za-z0-9+.-]*:?\/{0,2}$/.test(value) ||
    /^(?:[A-Za-z0-9-]+\.)*[A-Za-z0-9-]+\.?$/.test(value) ||
    /^\\{1,2}$/.test(value)
  );
}

function presentParsed(target: LaunchTarget): LaunchTargetPresentation {
  switch (target.kind) {
    case 'link':
      return {
        tone: 'ok',
        key: `${KEY_PREFIX}.recognised.link`,
        params: { launcher: LAUNCHER_LABELS[target.launcher], ref: target.ref },
      };
    case 'file':
      return {
        tone: 'ok',
        key: target.shortcut
          ? `${KEY_PREFIX}.recognised.shortcut`
          : `${KEY_PREFIX}.recognised.file`,
      };
    case 'invalid':
      return { tone: 'problem', key: problemKey(target.problem), problem: target.problem };
  }
}

/**
 * The field name a device's override error is filed under in a drawer's
 * error list — one namespace for both the pre-submit check and the API's
 * refusal once it has been mapped back to its device.
 */
export function overrideField(deviceId: string): string {
  return `override:${deviceId}`;
}

/**
 * Device ids in the order `toDevicePathPayload` emits them — blanks dropped,
 * insertion order kept. The API's `devicePaths.<index>.path` refers to THIS
 * order, so the mapper and the payload builder must share it.
 */
export function payloadDeviceIds(overrides: Record<string, string>): string[] {
  return Object.entries(overrides)
    .filter(([, path]) => path.trim().length > 0)
    .map(([deviceId]) => deviceId);
}

/**
 * The refusals a drawer raises BEFORE calling the API: the default path and
 * every non-blank override are run through the parser, and each problem is
 * filed under its own field with the problem's locale key (the caller
 * translates). An empty list means "go ahead and submit".
 *
 * A blank default is fine here — "no path yet" is a state the API accepts —
 * and a blank override is not sent at all, so neither is checked.
 */
export function launchTargetFieldProblems(
  defaultPath: string,
  overrides: Record<string, string>,
  /**
   * The devices the editor is showing rows for. An override for any other
   * device (hidden or retired, so absent from the drawer's device list) has
   * no row to carry its message, so its problem is filed under `(root)` and
   * names the device — a Save that is refused must always say why.
   */
  visibleDeviceIds?: readonly string[],
): { field: string; key: string; params?: Record<string, string> }[] {
  const problems: { field: string; key: string; params?: Record<string, string> }[] = [];
  const main = presentLaunchTarget(defaultPath);
  if (main.tone === 'problem') problems.push({ field: 'executablePath', key: main.key });
  for (const deviceId of payloadDeviceIds(overrides)) {
    const row = presentLaunchTarget(overrides[deviceId] ?? '');
    if (row.tone !== 'problem') continue;
    if (visibleDeviceIds !== undefined && !visibleDeviceIds.includes(deviceId)) {
      problems.push({
        field: '(root)',
        key: `${KEY_PREFIX}.problem.unlistedDevice`,
        params: { deviceId, value: overrides[deviceId] ?? '' },
      });
      continue;
    }
    problems.push({ field: overrideField(deviceId), key: row.key });
  }
  return problems;
}

/** The override refusals as `{ deviceId: message }`, for the editor's rows. */
export function overrideErrorMap(errors: FieldError[]): Record<string, string> {
  const prefix = overrideField('');
  const map: Record<string, string> = {};
  for (const issue of errors) {
    if (!issue.field.startsWith(prefix)) continue;
    const deviceId = issue.field.slice(prefix.length);
    if (!(deviceId in map)) map[deviceId] = issue.message;
  }
  return map;
}

const DEVICE_PATH_FIELD = /^devicePaths\.(\d+)(?:\.(.+))?$/;

/**
 * Re-file the API's `devicePaths.<index>.path` refusals under the device
 * whose row produced that entry, so the message shows beside the row rather
 * than nowhere. An index the payload never had (a stale response, or a
 * different payload shape) stays as it was rather than landing on a random
 * row. Every other error passes through untouched.
 */
export function mapDevicePathFieldErrors(
  errors: FieldError[],
  overrides: Record<string, string>,
  /** As in `launchTargetFieldProblems`: a device with no row reports at the root, by id. */
  visibleDeviceIds?: readonly string[],
): FieldError[] {
  const ids = payloadDeviceIds(overrides);
  return errors.map((error) => {
    const match = DEVICE_PATH_FIELD.exec(error.field);
    if (!match) return error;
    const deviceId = ids[Number(match[1])];
    if (deviceId === undefined) return error;
    if (visibleDeviceIds !== undefined && !visibleDeviceIds.includes(deviceId)) {
      return { field: '(root)', message: `${deviceId}: ${error.message}` };
    }
    return { ...error, field: overrideField(deviceId) };
  });
}

/**
 * The "Launcher / company" field fills itself from a recognised link, but
 * only while it is blank — or still holds the value this filled in last
 * time, so pasting a different launcher's link updates it. Anything staff
 * typed themselves is never overwritten. Returns the value the field should
 * hold, or `null` to leave it alone.
 */
export function suggestCompany(
  current: string,
  lastSuggestion: string | null,
  value: string,
): string | null {
  const untouched = current.trim() === '' || current === lastSuggestion;
  if (!untouched) return null;
  const target = parseLaunchTarget(value);
  if (target.kind !== 'link') return null;
  const label = LAUNCHER_LABELS[target.launcher];
  return label === current ? null : label;
}
